import { Component, Inject, Injector, OnInit, ChangeDetectionStrategy } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { forkJoin } from "rxjs";
import { getEnumLookup } from "src/app/_helpers/enum-helper";
import { toOrganizationTree } from "src/app/_helpers/org-tree-helper";
import { OperatorTypeEnum } from "src/app/enums/operatorType.enum";
import { OrgLevelTypeEnum } from "src/app/enums/orgLevelType.enum";
import { PpeType } from "src/app/enums/ppeType.enum";
import { RatingTypeEnum } from "src/app/enums/ratingTypeEnum.enum";
import { OrganizationLevel } from "src/app/models/organizationLevel.model";
import { Ppe } from "src/app/models/ppe.model";
import { CommonService } from "src/app/services/common.service";
import { OrganizationLevelService } from "src/app/services/organizationLevel.service";
import { PpeService } from "src/app/services/ppe.service";
import {
    FormComponent,
    IFormDialogData,
} from "src/app/shared/form/form.component";
import {
    HierarchicalSelectionFormField,
    NumberFormField,
    SingleSelectionFormField,
    TextAreaFormField,
    TextFormField,
} from "src/app/shared/form/formField.model";
import { ILookup } from "src/app/shared/form/lookup.model";
import nameOf from "src/app/shared/form/nameOf.helper";

@Component({
  standalone: false,
    selector: "app-ppe-dialog",
    templateUrl: "./../../shared/form/form.component.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ["./../../shared/form/form.component.scss"],
})
export class PpeDialogComponent extends FormComponent<Ppe> implements OnInit {
    organizationLevels: OrganizationLevel[];
    ppeTypes: ILookup[] = [
        {
            id: PpeType.Respiratory,
            name: this.commonService.getEnumName(PpeType, PpeType.Respiratory),
        },
        {
            id: PpeType.Noise,
            name: this.commonService.getEnumName(PpeType, PpeType.Noise),
        },
        {
            id: PpeType.Other,
            name: this.commonService.getEnumName(PpeType, PpeType.Other),
        },
    ];

    constructor(
        dialogRef: MatDialogRef<PpeDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: IFormDialogData,
        ppeService: PpeService,
        private commonService: CommonService,
        private organizationLevelService: OrganizationLevelService,
        protected injector: Injector,
    ) {
        super(dialogRef, ppeService, data, "PPE", injector);
    }

    ngOnInit(): void {
        this.isFormLoading = true;
        const orgPayload = JSON.stringify({
            OperatorTypeEnum: JSON.stringify({
                Condition: OperatorTypeEnum.Equal,
                Field: nameOf<OrganizationLevel>((x) => x.orgLevelType),
                Value: `${OrgLevelTypeEnum.Site},${OrgLevelTypeEnum.Function},${OrgLevelTypeEnum.BusinessUnit},${OrgLevelTypeEnum.Corporate}`,
            }),
        });
        forkJoin({
            entity: this.getEntity(),
            organizationLevels:
                this.organizationLevelService.getAll(orgPayload),
        }).subscribe(
            (result) => {
                this.entity = result.entity;
                this.organizationLevels = result.organizationLevels;
                this.initFields();
                this.isFormLoading = false;
            },
            (error) => {
                this.toastService.showError(error);
                this.isFormLoading = false;
            },
        );
    }

    getNewObject(): Ppe {
        return new Ppe();
    }

    validate(): boolean {
        return true;
    }

    initFields(): void {
        this.fields = [
            new TextFormField({
                property: nameOf<Ppe>((x) => x.name),
                name: "PPE Name",
            }),
            new SingleSelectionFormField({
                property: nameOf<Ppe>((x) => x.type),
                options: this.ppeTypes,
                name: "PPE Type",
            }),
            new HierarchicalSelectionFormField({
                property: nameOf<Ppe>((x) => x.organizationLevelId),
                options: toOrganizationTree(this.organizationLevels),
                name: "Organization Level",
                required: true,
            }),
            new TextFormField({
                property: nameOf<Ppe>((x) => x.brand),
                name: "Brand",
                required: false,
            }),
            new TextFormField({
                property: nameOf<Ppe>((x) => x.model),
                name: "Model",
                required: false,
            }),
            new NumberFormField({
                property: nameOf<Ppe>((x) => x.rating),
                name: "Rating",
                required: false,
            }),
            new SingleSelectionFormField({
                property: nameOf<Ppe>((x) => x.ratingType),
                options: getEnumLookup(RatingTypeEnum),
                name: "Rating Type",
                required: false,
            }),
            new TextAreaFormField({
                property: nameOf<Ppe>((x) => x.description),
                name: "Description",
                required: false,
            }),
        ];
    }
}
